import type {
  InferContractRouterInputs,
  InferContractRouterOutputs,
} from '@orpc/contract'
import { oc } from '@orpc/contract'
import { z } from 'zod'
import { gameConfigSchema, gameKindSchema } from './game/config.ts'
import { creditsSchema, stakeSchema } from './game/credits.ts'
import {
  FEATURE_KIND_EXPANDING,
  FEATURE_KIND_MULTIPLIER,
} from './game/features.ts'
import {
  gambleSymbolSchema,
  lineSlotPlaySchema,
  spinProofSchema,
} from './game/wire.ts'
import {
  adminLanguageSchema,
  cabinetLanguageSchema,
  errorParamsSchema,
  idSchema,
  inheritableFields,
  inheritableInput,
  listQuerySchema,
  listResultSchema,
} from './shared/index.ts'

export * from './shared/index.ts'

// The settings' shape, limits and path format, for admin-web's
// configuration form (the values a field takes, issues at their field).
// The checks against the game's definition run in the backend (the
// engine's `checkGameSettings`).
export {
  BET_STEPS,
  checkSettingsShape,
  configPath,
  lineSlotSettingsSchema,
  MAX_BET_PER_LINE,
  MAX_FREE_GAMES_AWARD,
  MAX_GAMBLE_ATTEMPTS,
  MAX_PAY,
  MAX_WEIGHT,
  MIN_WEIGHT,
} from './game/config.ts'
export type { GameConfigIssue, LineSlotSettings } from './game/config.ts'

// A cabinet's credits are branded money, like the game wire's; the decimal
// text of an amount (`creditsFromDecimal`, `formatCredits`) is exact, no floats.
export {
  CREDIT_UNITS,
  credits,
  creditsFromDecimal,
  formatCredits,
} from './game/credits.ts'
// What a round's detail is made of, for admin-web's round view: the plays of
// the round as the game server answered them, and the proof of each draw.
export type { Credits, Stake } from './game/credits.ts'
export {
  decodeExpandingSymbolData,
  decodeMultiplierFeatureData,
  FEATURE_KIND_EXPANDING,
  FEATURE_KIND_MULTIPLIER,
} from './game/features.ts'
export type {
  ExpandingSymbolData,
  MultiplierFeatureData,
} from './game/features.ts'
export { gambleSymbolToColour } from './game/wire.ts'
export type {
  ExpandingWin,
  FeatureUpdate,
  GambleLadder,
  GamblePick,
  GambleSymbol,
  LineSlotPlay,
  PaylineWin,
  ScatterWin,
  SpinProof,
  SpinResult,
} from './game/wire.ts'

export const adminUserSchema = z.object({
  id: idSchema,
  email: z.email(),
  name: z.string(),
})

export const membershipSchema = z.object({
  tenantId: idSchema,
  /** organization plugin role: `owner`, `admin` or `member` */
  role: z.string(),
})

/**
 * A user's role on the platform (Better Auth's admin-plugin `role`), or none:
 * a tenant user. A platform admin is a super admin: it manages every tenant,
 * user, game and configuration, and has every technician power. A technician
 * sets cabinets up on the bench and on site: it reads every tenant, its
 * locations and cabinets and manages the cabinets, and reaches nothing else.
 * One user holds at most one.
 */
export const PLATFORM_ROLES = ['admin', 'technician'] as const
export const platformRoleSchema = z.enum(PLATFORM_ROLES)
export type PlatformRole = z.output<typeof platformRoleSchema>

/**
 * The host the panel runs on, so the session can name the host tenant's
 * default admin language: the tenant's slug, null or omitted on the apex.
 * Another tenant's slug counts as none.
 */
export const sessionInputSchema = z
  .object({ tenantSlug: z.string().nullable().optional() })
  .optional()

export const panelLanguageInputSchema = z.object({
  tenantSlug: z.string().nullable(),
})

export const panelLanguageSchema = z.object({
  adminLanguage: adminLanguageSchema,
})

export const myLanguageInputSchema = z.object({
  /** null: back to Use default */
  language: adminLanguageSchema.nullable(),
  tenantSlug: z.string().nullable().optional(),
})

export const sessionInfoSchema = z.object({
  user: adminUserSchema,
  /** the user's platform role; null: a tenant user */
  platformRole: platformRoleSchema.nullable(),
  /** tenants the user is a member of */
  tenantIds: z.array(idSchema),
  /** the user's role in each of those tenants */
  memberships: z.array(membershipSchema),
  /**
   * the panel language the user chose; null: Use default, which is
   * `inheritedLanguage` (the host tenant's default admin language, else the
   * platform's, else the request's `Accept-Language`, else en)
   */
  ...inheritableFields('language', adminLanguageSchema),
  /** the language the panel shows: `language`, else `inheritedLanguage` */
  adminLanguage: adminLanguageSchema,
})

/** Tenant roles that may read their tenant's activity log (staff always may). */
export const ACTIVITY_VIEWER_ROLES = ['owner', 'admin'] as const

/**
 * Tenant roles that may credit in and cash out their tenant's cabinets
 * (staff always may).
 */
export const CABINET_CREDIT_ROLES = ['owner', 'admin'] as const

/** Tenant roles that may read their tenant's rounds (staff always may). */
export const ROUND_VIEWER_ROLES = ['owner', 'admin'] as const

/** Tenant roles that may read their tenant's dashboard (staff always may). */
export const DASHBOARD_VIEWER_ROLES = ['owner', 'admin'] as const

/**
 * Tenant roles that may open their tenant's Settings (staff always may);
 * only `TENANT_UPDATE_ROLES` change anything there.
 */
export const TENANT_SETTINGS_VIEWER_ROLES = ['owner', 'admin'] as const

/**
 * Tenant roles that may change their tenant's name and default language
 * (`tenants.update`; staff always may, and only staff change its slug or
 * status).
 */
export const TENANT_UPDATE_ROLES = ['owner'] as const

/**
 * Tenant roles that may read their tenant's members and open invitations,
 * invite people to it, change its members' roles, remove them and revoke
 * its invitations (`members.*`, `invitations.list` / `create` / `revoke`;
 * staff always may). Owners stay with staff: these roles never change or
 * remove an owner, nor revoke an invitation as owner.
 */
export const MEMBER_MANAGER_ROLES = ['owner', 'admin'] as const

/**
 * The roles a tenant's owners and admins give: they invite with them and set
 * them on members (`members.setRole`, staff too). Only staff make someone
 * `owner` (`users.setMembership`, or an invitation as owner).
 */
export const TENANT_GRANT_ROLES = ['admin', 'member'] as const

/**
 * Tenant roles that may list the configurations their tenant may run
 * (`games.configurations.list`: the platform's and their tenant's own) and
 * pick the configuration a cabinet of their tenant runs (`gameConfigs` on
 * `cabinets.create` / `update`); staff always may. Everything else about
 * games and configurations (reading a game or a whole configuration,
 * creating, copying, checking, editing and simulating one) is staff only.
 */
export const GAME_CONFIGURATION_ROLES = ['owner', 'admin'] as const

/**
 * Tenant roles that may read and set their tenant's default configuration
 * of each game (`tenants.defaultConfigurations.get` / `update`), which its
 * cabinets on Use default boot; staff always may.
 */
export const TENANT_DEFAULT_CONFIGURATION_ROLES = ['owner', 'admin'] as const

/** the longest name a record may have */
export const MAX_NAME_LENGTH = 200

const nameSchema = z.string().trim().min(1).max(MAX_NAME_LENGTH)

/** Subdomains of the admin domain that are not tenants. */
export const RESERVED_SLUGS = [
  'api',
  'www',
  'app',
  'admin',
  'login',
  'static',
  'mail',
] as const

/**
 * lowercase letters and digits, words joined by single hyphens:
 * `spielhalle-nord`. A slug is the tenant's subdomain, so it is one DNS label
 * (max 63) and never one of the reserved infrastructure names.
 */
export const slugSchema = z
  .string()
  .min(1)
  .max(63)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase words joined by hyphens')
  .refine(
    (slug) => !(RESERVED_SLUGS as readonly string[]).includes(slug),
    'this name is reserved',
  )

export const tenantStatusSchema = z.enum(['active', 'suspended'])

export const tenantSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  /**
   * the language its cabinets on Default play in; null: Use default, which is
   * `inheritedDefaultLanguage` (the platform's default language)
   */
  ...inheritableFields('defaultLanguage', cabinetLanguageSchema),
  /**
   * the language the panel shows its users on Default; null: Use default,
   * which is `inheritedAdminLanguage` (the platform's default admin
   * language; null while it has none: the browser's language, else en)
   */
  ...inheritableFields(
    'adminLanguage',
    adminLanguageSchema,
    adminLanguageSchema.nullable(),
  ),
  status: tenantStatusSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
})

/** The id of the platform's one settings record (its activity log entries). */
export const PLATFORM_SETTINGS_ID = '00000000-0000-4000-8000-000000000001'

/**
 * The platform's own settings: the defaults every tenant inherits until it
 * sets its own.
 */
export const platformSettingsSchema = z.object({
  /**
   * the language cabinets play in while they and their tenant are on
   * Default; null: Use default, which is `inheritedDefaultLanguage` (built
   * in: en)
   */
  ...inheritableFields('defaultLanguage', cabinetLanguageSchema),
  /**
   * the language the panel shows while a user and their tenant are on
   * Default; null: Use default, which is `inheritedAdminLanguage` (null: the
   * browser's language, else en)
   */
  ...inheritableFields(
    'adminLanguage',
    adminLanguageSchema,
    adminLanguageSchema.nullable(),
  ),
})

/**
 * A user's role in a tenant (the organization plugin's roles). Only staff
 * make someone `owner` (`users.setMembership`, or `invitations.create` once
 * the invitee accepts); a tenant's owners and admins invite with and set
 * `TENANT_GRANT_ROLES`.
 */
export const TENANT_ROLES = ['owner', 'admin', 'member'] as const
export const tenantRoleSchema = z.enum(TENANT_ROLES)

/** A password staff set for a new user: Better Auth's default limits. */
export const MIN_PASSWORD_LENGTH = 8
export const MAX_PASSWORD_LENGTH = 128

/** The longest reason a ban may give. */
export const MAX_BAN_REASON_LENGTH = 500

/** A user's role in one tenant, with the tenant, as staff read it. */
export const platformUserMembershipSchema = z.object({
  tenantId: idSchema,
  tenantName: z.string(),
  tenantSlug: z.string(),
  /** organization plugin role: `owner`, `admin` or `member` */
  role: z.string(),
})

/**
 * An admin account as staff see it (`users.list` / `users.get`). Users are
 * global identities: one account, a member of any number of tenants.
 */
export const platformUserSchema = z.object({
  id: idSchema,
  name: z.string(),
  email: z.string(),
  /** the user's platform role (Better Auth's admin role); null: a tenant user */
  platformRole: platformRoleSchema.nullable(),
  /** banned now: a ban whose `banExpires` has passed is over */
  banned: z.boolean(),
  /** while banned: why, if a reason was given */
  banReason: z.string().nullable(),
  /** while banned: when the ban ends; null for a ban without an end */
  banExpires: z.date().nullable(),
  /** the user's last own sign-in (an impersonation is not one); null: never */
  lastSignInAt: z.date().nullable(),
  /** the tenants the user is a member of, by tenant name */
  memberships: z.array(platformUserMembershipSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
})

/** An invitation's state: open until the invitee accepts or rejects it, or it is revoked. */
export const INVITATION_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'canceled',
] as const

/** An invitation to join a tenant (Better Auth's `invitations`). */
export const invitationSchema = z.object({
  id: idSchema,
  tenantId: idSchema,
  /** lower case: the invitee signs up or in with it */
  email: z.string(),
  /** the role the invitee joins the tenant with */
  role: tenantRoleSchema,
  status: z.enum(INVITATION_STATUSES),
  /** a pending invitation can no longer be accepted after this */
  expiresAt: z.date(),
  createdAt: z.date(),
})

/**
 * The header with which admin-web proves, over the auth API, that it holds
 * an invitation's link: `<invitation id>.<token>` (the link's `token`).
 * Better Auth's sign-up, accept-invitation and reject-invitation refuse a
 * request without the right one (FORBIDDEN): the id alone shows in the
 * tenant's activity log, the token reaches only the invitee's mailbox.
 */
export const INVITATION_HEADER = 'x-sirius-invitation'

/**
 * The code of the auth API's CONFLICT (409) for accepting an invitation to a
 * tenant the user is a member of already (staff may have added them since).
 */
export const ALREADY_A_MEMBER = 'ALREADY_A_MEMBER_OF_THE_TENANT'

/** the longest token an invitation's link may carry (it is ~43 characters) */
const MAX_INVITATION_TOKEN_LENGTH = 200

/**
 * What the invitation's page shows: `expired` for a pending invitation past
 * `expiresAt` (it can no longer be accepted), otherwise its status.
 */
export const INVITATION_PREVIEW_STATUSES = [
  ...INVITATION_STATUSES,
  'expired',
] as const

/**
 * An invitation as its link shows it to whoever opens it, signed in or not
 * (`invitations.preview`): the link's token, mailed to the invitee only, is
 * the key to it.
 */
export const invitationPreviewSchema = z.object({
  tenantName: z.string(),
  /** the tenant's subdomain: admin-web opens the tenant there once joined */
  tenantSlug: z.string(),
  /** lower case: the invitee signs up or in with it */
  email: z.string(),
  role: tenantRoleSchema,
  status: z.enum(INVITATION_PREVIEW_STATUSES),
  expiresAt: z.date(),
  /**
   * while `pending`: whether the email already has an account (the invitee
   * signs in) or not (they sign up); always false otherwise
   */
  hasAccount: z.boolean(),
})

/** A member of a tenant: the user and their role there. */
export const tenantMemberSchema = z.object({
  /** the membership's id */
  id: idSchema,
  userId: idSchema,
  name: z.string(),
  email: z.string(),
  role: tenantRoleSchema,
  /** when they joined the tenant */
  createdAt: z.date(),
})

export const locationSchema = z.object({
  id: idSchema,
  tenantId: idSchema,
  name: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const cabinetStatusSchema = z.enum([
  'active',
  'disabled',
  'decommissioned',
])

const configVersionSchema = z.number().int().min(1)

/**
 * The configuration a cabinet plays one of its games on, and the config
 * version it runs. Staff and the tenant's owners/admins
 * (`GAME_CONFIGURATION_ROLES`) may assign the cabinet any configuration of
 * the game its tenant may run (`configurationId`: the platform's or the
 * tenant's own, published ones only); without one it plays on Use default:
 * its tenant's default configuration of the game
 * (`tenants.defaultConfigurations`), else the game's. The cabinet boots the
 * current published version of that configuration (never a draft), and a
 * change (a new assignment, a new default, a publish of the configuration)
 * reaches it on its next boot with no round open, so the assigned and the
 * running version differ until then (pending until restart). Nothing is
 * pushed live. A version number counts the publishes of its configuration,
 * so it goes with the configuration's name.
 */
export const cabinetGameConfigSchema = z.object({
  gameId: idSchema,
  /**
   * the configuration assigned to this cabinet; null: Use default, which is
   * `inheritedConfigurationId` (its tenant's default configuration of the
   * game, else the game's; null while neither has a current version)
   */
  ...inheritableFields('configurationId', idSchema, idSchema.nullable()),
  /**
   * the version the cabinet's next boot of the game plays: the current
   * version of the assigned configuration, else of its tenant's default,
   * else of the game's default; null while none has one
   */
  assignedConfigVersion: configVersionSchema.nullable(),
  /** the name of the configuration of `assignedConfigVersion` */
  assignedConfigurationName: z.string().nullable(),
  /** when `assignedConfigVersion` was saved */
  assignedConfigSavedAt: z.date().nullable(),
  /**
   * the version the cabinet's game session runs; null unless the session
   * plays this game (one game at a time)
   */
  runningConfigVersion: configVersionSchema.nullable(),
  /** the name of the configuration of `runningConfigVersion` */
  runningConfigurationName: z.string().nullable(),
  /** when `runningConfigVersion` was saved */
  runningConfigSavedAt: z.date().nullable(),
})

export const cabinetSchema = z.object({
  id: idSchema,
  tenantId: idSchema,
  /** null while the cabinet is in storage */
  locationId: idSchema.nullable(),
  name: z.string(),
  /**
   * the language its games play in (a game without it plays en); null:
   * Default, which is `inheritedLanguage` (its tenant's default language,
   * else the platform's)
   */
  ...inheritableFields('language', cabinetLanguageSchema),
  status: cabinetStatusSchema,
  lastSeenAt: z.date().nullable(),
  /** null until a key is issued (`cabinets.issueKey`) */
  keyIssuedAt: z.date().nullable(),
  /** the games the cabinet offers its players (sorted); empty: none */
  gameIds: z.array(idSchema),
  /** the configuration and config version of each of its games, in the order of `gameIds` */
  gameConfigs: z.array(cabinetGameConfigSchema),
  /** the cabinet's balance: the last entry of its credits ledger (0 without one) */
  credits: creditsSchema,
  /**
   * no round is open on the cabinet (no win to settle, no feature, no gamble
   * ladder): only then may its credits change (`creditIn`, `cashOut`)
   */
  idle: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

/**
 * The amounts an operator credits in with, in stored units (hundredths of a
 * credit): 0.10, 0.20, 0.50, 1, 2, 5, 10, 20, 50 and 100 credits. Every
 * other amount (0.30, 0.505, 1 000 credits) is refused.
 */
export const CREDIT_IN_AMOUNTS = [
  10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10_000,
] as const

/** The most one credit-in may add: the largest of `CREDIT_IN_AMOUNTS`. */
export const MAX_CREDIT_IN = CREDIT_IN_AMOUNTS[CREDIT_IN_AMOUNTS.length - 1]

export const cabinetCreditInInputSchema = z.object({
  id: idSchema,
  /** stored units (hundredths of a credit), one of `CREDIT_IN_AMOUNTS` */
  amount: z
    .int({ error: 'whole hundredths of a credit' })
    .refine(
      (amount) => (CREDIT_IN_AMOUNTS as readonly number[]).includes(amount),
      {
        error: `one of ${CREDIT_IN_AMOUNTS.join(', ')} (hundredths of a credit)`,
      },
    )
    .pipe(creditsSchema),
})

/**
 * `credits`: the balance the operator saw and confirmed. The cash-out pays
 * exactly that, or is refused (CABINET_CREDITS_CHANGED) when the cabinet's
 * balance is no longer it.
 */
export const cabinetCashOutInputSchema = z.object({
  id: idSchema,
  credits: creditsSchema,
})

/**
 * Why a cabinet's credits may not change now, as `creditIn`'s and
 * `cashOut`'s typed errors. oRPC marks a thrown error as defined only when
 * its status is the one here, so the backend throws it with that status. No
 * data schema: a data-less throw would no longer be defined.
 */
export const CABINET_CREDIT_ERRORS = {
  CABINET_NOT_IDLE: {
    status: 409,
    message: 'The cabinet is not idle: a round is open',
  },
  CABINET_NOT_ACTIVE: {
    status: 409,
    message: 'The cabinet is not active',
  },
  /** a cash-out: the balance is no longer the one the operator confirmed */
  CABINET_CREDITS_CHANGED: {
    status: 409,
    message:
      "The cabinet's credits changed: check the balance and confirm again",
  },
} as const

/** A refusal of `creditIn` / `cashOut`. */
export type CabinetCreditErrorCode = keyof typeof CABINET_CREDIT_ERRORS

/**
 * A game of the platform, e.g. Book of Ra 2. Its configs are named
 * configurations, one of them the game's default.
 */
export const gameSchema = z.object({
  id: idSchema,
  /** the cabinet's key for the game's bundled assets, e.g. `book-of-ra-2` */
  slug: z.string(),
  name: z.string(),
  kind: gameKindSchema,
  /**
   * the configuration cabinets run unless they were assigned another one or
   * their tenant set a default configuration of the game
   */
  defaultConfigurationId: idSchema,
  defaultConfigurationName: z.string(),
  /** the version the game runs on: the default configuration's current one */
  configId: idSchema,
  /** that version's number in its configuration */
  configVersion: z.number().int().min(1),
  createdAt: z.date(),
  updatedAt: z.date(),
})

/**
 * Symbols that share one pay per count, as the game's paytable shows them
 * together (A/K): a configuration sets the pay of each of `counts`
 * (`settings.pays.<key>.<count>`), and each of its symbols pays that.
 */
export const payGroupSchema = z.object({
  /** the key of its pays in the settings, e.g. `explorer` */
  key: z.string().min(1),
  /** what admin-web calls it, e.g. "A/K" */
  label: z.string(),
  /** its symbols' ids */
  symbols: z.array(z.string().min(1)).min(1),
  /** the counts that pay, as the definition lists them, e.g. [5, 4, 3, 2] */
  counts: z.array(z.int().min(1)).min(1),
})

/**
 * What a game fixes in code (its definition in `@sirius/game-engine`), as
 * the configuration form needs it to offer only the values the game
 * allows: its grid, its symbols (id, admin-web's name, traits: empty for a
 * regular symbol), its pay groups, its count of lines (a configuration
 * offers line counts 1 to it) and what it fixes of its free spins (their
 * kind, the trigger symbol and the most games one feature awards, none
 * without `maxTotalGames`; null for a game without free spins). A
 * configuration's settings go on top of it.
 */
export const gameDefinitionSchema = z.object({
  reelCount: z.int().min(1),
  rowCount: z.int().min(1),
  symbols: z
    .array(
      gameConfigSchema.shape.symbols.element
        .pick({ id: true, traits: true })
        .extend({ label: z.string() }),
    )
    .min(1),
  payGroups: z.array(payGroupSchema).min(1),
  lineCount: z.int().min(1),
  freeSpins: gameConfigSchema.shape.freeSpins
    .unwrap()
    .pick({
      featureKind: true,
      triggerSymbolId: true,
      maxTotalGames: true,
      winMultiplier: true,
    })
    .nullable(),
})

/**
 * A game as `games.get` reads it: with its definition (`games.list` leaves
 * it out), null for a game the backend has no definition of.
 */
export const gameDetailSchema = gameSchema.extend({
  definition: gameDefinitionSchema.nullable(),
})

/** the longest note a configuration may carry */
export const MAX_CONFIG_NOTE_LENGTH = 500

/**
 * One reason a configuration's settings are refused. `path` leads from the
 * procedure's input, so it starts with `settings` (`['settings', 'reels',
 * 2, 7]`), the way oRPC's own input validation reports an issue.
 */
export const gameConfigIssueSchema = z.object({
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
  /** absent from a backend older than the codes; the panel then shows `message` */
  code: z.string().optional(),
  params: errorParamsSchema.optional(),
})

/**
 * `data` of the BAD_REQUEST `games.configurations.publish` and
 * `analysis.start` throw for refused settings
 */
export const gameConfigIssuesSchema = z.object({
  issues: z.array(gameConfigIssueSchema).min(1),
})

/**
 * Who saved a configuration; null for the seed or a deleted user. Tenant
 * users get null for a staff user too: they never learn who on the platform
 * staff acted, only who of their own tenant did.
 */
const configAuthorSchema = z
  .object({ id: idSchema, name: z.string(), email: z.email() })
  .nullable()

/**
 * A published version of a configuration. Immutable: every publish of a
 * draft is a new version, so a round keeps pointing at exactly what it was
 * played on.
 */
export const gameConfigVersionSchema = z.object({
  id: idSchema,
  /** 1 for the configuration's first publish, then one more per publish */
  version: z.number().int().min(1),
  /** the configuration's note when it was published */
  note: z.string(),
  /**
   * the RTP (a fraction, 0.9612) the engine works out for its settings on
   * the game's definition: base game and free spins, gamble left out. null
   * when not computed.
   */
  analyticRtp: z.number().nullable(),
  /** who published it; null for a seeded version or a deleted user (see `configAuthorSchema`) */
  createdBy: configAuthorSchema,
  /** when it was published */
  createdAt: z.date(),
})

/**
 * The published version a draft started from: one of its own
 * configuration's, or the source's for a copy. null for a draft of new
 * settings (a configuration created from `settings`).
 */
export const configDraftBaseSchema = z
  .object({
    /** that version's configuration: another one than the draft's for a copy */
    configurationId: idSchema,
    /** the name of that version's configuration (its current name) */
    configurationName: z.string(),
    version: z.number().int().min(1),
  })
  .nullable()

/**
 * The configuration's draft as a list shows it: staff's work in progress,
 * which no cabinet runs until it is published. At most one per
 * configuration.
 */
export const gameConfigDraftSummarySchema = z.object({
  /**
   * numbers the draft's saves: one more per save, counting on across the
   * configuration's drafts (a new one after a publish or a discard does not
   * start over), so a revision names one save of one draft. A save, publish
   * or discard sends it as `expectedRevision`
   */
  revision: z.number().int().min(1),
  basedOn: configDraftBaseSchema,
  /** who saved it last; null for a deleted user (see `configAuthorSchema`) */
  savedBy: configAuthorSchema,
  savedAt: z.date(),
})

/**
 * The configuration's draft in full: the whole editor state (name, note,
 * settings) staff saved, which may be incomplete or fail the checks
 * (`issues`). Its name and note apply on publish.
 */
export const gameConfigDraftSchema = gameConfigDraftSummarySchema.extend({
  /** the draft's row: `analysis.start` takes it as `configId` */
  id: idSchema,
  /** what the configuration is named once the draft is published */
  name: z.string(),
  note: z.string(),
  /** `LineSlotSettings` as JSON, as saved (any object) */
  settings: z.record(z.string(), z.unknown()),
  /**
   * what a publish would refuse, checked when the draft is read (paths start
   * with `settings`); none when it can be published
   */
  issues: z.array(gameConfigIssueSchema),
  /** the draft's analytic RTP; null while it has issues */
  analyticRtp: z.number().nullable(),
})

/**
 * A named configuration of a game ("Default configuration"). Staff save
 * changes in its draft and publish it, which writes its next version
 * (`currentVersion`) and applies the draft's name and note. Cabinets only
 * ever run its current published version. It belongs to the platform
 * (staff edit it, every tenant's cabinets may run it, it may be the game's
 * default) or to one tenant (staff edit it, only that tenant sees it in its
 * picker and its cabinets run it). One never published (a new one or a
 * copy, with a draft only) cannot be picked for a cabinet nor be a default.
 */
export const gameConfigurationSchema = z.object({
  id: idSchema,
  gameId: idSchema,
  /** the tenant it belongs to; null for a platform configuration */
  tenantId: idSchema.nullable(),
  /** that tenant's name; null for a platform configuration */
  tenantName: z.string().nullable(),
  /** unique among the game's configurations of the same owner */
  name: z.string(),
  /** what it is for */
  note: z.string(),
  /**
   * its current published version: what cabinets running it boot, what
   * simulations and seed searches take; null: never published
   */
  currentConfigId: idSchema.nullable(),
  /** that version's number; null: never published */
  currentVersion: z.number().int().min(1).nullable(),
  /** the current version's analytic RTP (see `gameConfigVersionSchema`); null: never published */
  analyticRtp: z.number().nullable(),
  /** whether it is the game's default configuration */
  isDefault: z.boolean(),
  /** its draft; null without one, and always null for tenant users */
  draft: gameConfigDraftSummarySchema.nullable(),
  /** who changed it last; null for a seeded or migrated one or a deleted user (see `configAuthorSchema`) */
  updatedBy: configAuthorSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
})

/**
 * A configuration with its current published settings, its draft in full
 * and every published version, newest first (its save history), as
 * `games.configurations.get`, `create`, `saveDraft`, `publish` and
 * `discardDraft` return it.
 */
export const gameConfigurationDetailSchema = gameConfigurationSchema.extend({
  /**
   * the current version's settings (`LineSlotSettings`) as JSON, checked
   * when they were published; null: never published
   */
  settings: z.record(z.string(), z.unknown()).nullable(),
  draft: gameConfigDraftSchema.nullable(),
  /** published versions only */
  versions: z.array(gameConfigVersionSchema),
})

/** The round counts a simulation may play. */
export const SIMULATION_ROUNDS = [100_000, 1_000_000, 10_000_000] as const

export const simulationRoundsSchema = z.literal(SIMULATION_ROUNDS)

/**
 * The most runs that may wait in a backend's one queue (more: CONFLICT).
 * Only staff start runs.
 */
export const MAX_QUEUED_SIMULATIONS = 10

/**
 * queued → running → done or failed; a cancelled run fails, also from
 * queued (`startedAt` null). One run plays at a time per backend
 * instance; the others wait as `queued` in one queue, oldest first. Only
 * staff start, see and cancel runs.
 */
export const analysisRunStatusSchema = z.enum([
  'queued',
  'running',
  'done',
  'failed',
])

/**
 * What a full-game simulation measured: whole rounds (a paid spin, its
 * feature, the collect) at the lowest bet on every payline, the gamble left
 * out. Rates are fractions (0.9612); `…X` are multiples of the total bet.
 */
export const simulationResultSchema = z.object({
  /** paid spins played (= rounds) */
  paidSpins: z.number().int().min(0),
  totalStaked: z.number().min(0),
  totalReturned: z.number().min(0),
  rtp: z.number(),
  /** the normal 95% interval of `rtp` over the returns of whole rounds */
  rtpCi95: z.object({ lower: z.number(), upper: z.number() }),
  /** paid-spin wins (trigger wins included) per credit staked */
  baseRtp: z.number(),
  /** free-spins wins per credit staked */
  featureRtp: z.number(),
  /** paid spins that won or triggered the feature */
  hitFrequency: z.number(),
  /** features triggered */
  features: z.number().int().min(0),
  /** features per paid spin */
  triggerFrequency: z.number(),
  /** features retriggered at least once, per feature */
  retriggerRate: z.number(),
  meanGamesPerFeature: z.number(),
  /** the average feature win */
  meanFeatureWinX: z.number(),
  maxFeatureWinX: z.number(),
  /** the biggest round win (paid spin + feature) */
  maxRoundWinX: z.number(),
})

/**
 * A simulation (`analysis.*`, staff only): a quick check of a published
 * version or of a configuration's saved draft. Runs wait in one queue,
 * oldest first, and live in the backend's memory only: a finished run is
 * dropped after a while, and every run with a restart.
 */
export const analysisRunSchema = z.object({
  id: idSchema,
  gameId: idSchema,
  /** the published version or the draft it simulates */
  configId: idSchema,
  /** the number of that version in its configuration; null for a draft */
  configVersion: z.number().int().min(1).nullable(),
  /**
   * the draft's revision whose settings it plays (a later save does not
   * change the run); null for a published version
   */
  draftRevision: z.number().int().min(1).nullable(),
  /** the name of that version's configuration (for a draft: the configuration's, not the draft's) */
  configurationName: z.string(),
  rounds: simulationRoundsSchema,
  /**
   * the fast draw's seed (`fastDraw`, not provably fair): `make simulate
   * ARGS='--fast --seed <seed> --rounds <rounds> --config <file> --game
   * <slug>'` replays the run, with its settings saved as `<file>` in
   * `packages/gameEngine/configs/`
   */
  seed: z.string(),
  status: analysisRunStatusSchema,
  /** the runs before it in the queue, the playing one included; 0 unless queued */
  queuePosition: z.number().int().min(0),
  /** rounds played / rounds, 0 to 1 (1 once done) */
  progress: z.number().min(0).max(1),
  /** set once done */
  result: simulationResultSchema.nullable(),
  /** why it failed (also that it was cancelled); set once failed */
  error: z.string().nullable(),
  /** the analytic RTP of the settings it plays, to compare */
  analyticRtp: z.number(),
  /** the staff user who started it */
  startedBy: z.object({ id: idSchema, name: z.string() }),
  createdAt: z.date(),
  startedAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
})

export const analysisStartInputSchema = z.object({
  /** a published config version or a configuration's saved draft (its `id`) */
  configId: idSchema,
  rounds: simulationRoundsSchema,
})

export const analysisListInputSchema = z.object({ gameId: idSchema })

/**
 * What the seed search looks for in a session's first paid spin, at a fresh
 * session's bet (the engine's `OUTCOMES`; a backend test keeps them equal):
 * a regular win, a feature, a loss, or a regular win the version lets the
 * player gamble whose first gamble card a red pick wins or loses.
 */
export const SEED_OUTCOMES = [
  'win',
  'freeSpins',
  'loss',
  'gambleWin',
  'gambleLoss',
] as const

export const seedOutcomeSchema = z.enum(SEED_OUTCOMES)

/** The most seeds one search returns. */
export const MAX_SEED_COUNT = 10

/**
 * The candidates a search tries (`seed-0` up to `seed-4999`), as `make
 * find-seeds` does by default, so both find the same seeds.
 */
export const SEED_SEARCH_LIMIT = 5000

export const seedSearchInputSchema = z.object({
  /** a published config version (a draft: BAD_REQUEST) */
  configId: idSchema,
  outcome: seedOutcomeSchema,
  /** seeds wanted, 1 to MAX_SEED_COUNT */
  count: z.number().int().min(1).max(MAX_SEED_COUNT),
})

/** What every found feature has, whatever its kind. */
const seedFeatureBase = {
  /** the trigger spin's own win */
  triggerWin: z.number().int().min(0),
  /** free games that awarded more games */
  retriggers: z.number().int().min(0),
  /** games awarded, retriggers included */
  total: z.number().int().min(0),
  featureWin: z.number().int().min(0),
  /** false when it still had games left after the engine's game limit */
  ended: z.boolean(),
}

/**
 * The feature a seed's spin opens, played through to its last game, by
 * feature kind: Book of Ra's names its expanding symbol, a multiplier
 * feature its win multiplier.
 */
export const seedFeatureSchema = z.discriminatedUnion('featureKind', [
  z.object({
    featureKind: z.literal(FEATURE_KIND_EXPANDING),
    ...seedFeatureBase,
    expandingSymbolId: z.string(),
  }),
  z.object({
    featureKind: z.literal(FEATURE_KIND_MULTIPLIER),
    ...seedFeatureBase,
    winMultiplier: z.number().int().min(1),
  }),
])

/** A seed whose session's first spin shows the outcome, and what it shows. */
export const foundSeedSchema = z.object({
  /** `seed-<n>`: a cabinet's `?seed=` (with ALLOW_TEST_SEEDS) */
  seed: z.string(),
  /** the spin's own win, in credits */
  totalWin: z.number().int().min(0),
  /** for `freeSpins`; else null */
  feature: seedFeatureSchema.nullable(),
  /** for a gamble outcome, the first card of the ladder; else null */
  card: gambleSymbolSchema.nullable(),
})

export const seedSearchResultSchema = z.object({
  /** in candidate order; fewer than `count` when the candidates ran out */
  seeds: z.array(foundSeedSchema),
})

/**
 * The credits a test cabinet starts with (booked as a credit-in): 10 000
 * credits in stored units. Not one of `CREDIT_IN_AMOUNTS`: the backend books
 * it itself, no operator typed it.
 */
export const TEST_CABINET_CREDITS = 1_000_000

export const testCabinetInputSchema = z.object({
  /**
   * the published version the cabinet plays (its configuration is assigned
   * to its one game; a draft: BAD_REQUEST)
   */
  configId: idSchema,
  /** the seed it is opened with, for its tenant's name (`seed-<n>`) */
  seed: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[\w.-]+$/),
})

/**
 * A new cabinet to play a seed on: its id and key (shown once, like
 * `cabinets.issueKey`), and its tenant of its own.
 */
export const testCabinetSchema = z.object({
  cabinetId: idSchema,
  /** the cabinet's secret key: `?cabinet=<cabinetId>.<key>` on a dev cabinet */
  key: z.string().min(1),
  tenantId: idSchema,
  tenantSlug: z.string(),
})

/** How this backend runs, for staff. */
export const systemInfoSchema = z.object({
  /**
   * ALLOW_TEST_SEEDS is set: a cabinet may pick the seed of a session the
   * backend starts for it (dev and e2e only, never in production)
   */
  allowTestSeeds: z.boolean(),
})

/**
 * `LineSlotSettings` without `reels`, as a draft save and the live check
 * take them: the backend owns the strips and builds them from `weights`
 * (`@sirius/game-engine`'s strip generator), so a request that sends `reels`
 * is refused.
 */
const settingsWithoutReelsSchema = z
  .record(z.string(), z.unknown())
  .refine((settings) => !Object.hasOwn(settings, 'reels'), {
    path: ['reels'],
    message: 'is built from the weights: leave it out',
  })

/** Settings a person is writing, to check before they are saved. */
export const gameConfigDraftInputSchema = z.object({
  /**
   * the configuration they are for: its draft, else its current published
   * version, gives the strips the weights change
   */
  id: idSchema,
  /**
   * `LineSlotSettings` as JSON without `reels` (refused if sent): any
   * object, checked like a publish checks it, on the strips the weights
   * would build
   */
  settings: settingsWithoutReelsSchema,
})

/** What `games.configurations.check` found in a draft. */
export const gameConfigCheckSchema = z.object({
  /**
   * every issue a publish would report (paths start with `settings`); none
   * when they can be published
   */
  issues: z.array(gameConfigIssueSchema),
  /** their analytic RTP, the strips the weights would build; null while they have issues */
  analyticRtp: z.number().nullable(),
  /** the part of it a paid spin's line and scatter wins make; null while they have issues */
  baseRtp: z.number().nullable(),
  /** the part the free games make; 0 without free games, null while they have issues */
  featureRtp: z.number().nullable(),
  /**
   * 1 in N paid spins trigger the free games; null for a game without free
   * games and while they have issues
   */
  triggerOneIn: z.number().nullable(),
  /**
   * the engine's config combined from the game's definition and these
   * settings (`gameConfigOf`) and the strips the weights would build, also
   * while they break a rule between fields; null when they cannot be
   * combined (a shape issue, no definition)
   */
  engineConfig: gameConfigSchema.nullable(),
})

/**
 * The RTP a weight solve may miss its target by: ±0.5 percentage points
 * (a fraction of the bet, like every RTP here). One weight step moves a
 * stop on every reel, so a solve cannot always be exact.
 */
export const RTP_SOLVE_TOLERANCE = 0.005

/** The target RTPs a solve takes, as fractions: 50 % to 100 %. */
export const MIN_TARGET_RTP = 0.5
export const MAX_TARGET_RTP = 1

/** What `analysis.solve` takes: the live check's input and a target RTP. */
export const weightSolveInputSchema = gameConfigDraftInputSchema.extend({
  /** the RTP to aim at, a fraction (0.96), from MIN_TARGET_RTP to MAX_TARGET_RTP; a solver input only, never stored */
  targetRtp: z.number().min(MIN_TARGET_RTP).max(MAX_TARGET_RTP),
})

/**
 * The RTP of a solve's weights, on the strips a save builds from them: what
 * `games.configurations.check` shows once the draft is saved.
 */
export const weightSolveRtpSchema = z.object({
  /** their analytic RTP: base game and free games */
  analyticRtp: z.number(),
  /** the part a paid spin's line and scatter wins make */
  baseRtp: z.number(),
  /** the part the free games make; 0 without free games */
  featureRtp: z.number(),
  /** 1 in N paid spins trigger the free games; null for a game without free games */
  triggerOneIn: gameConfigCheckSchema.shape.triggerOneIn,
})

/** What `analysis.solve` found. */
export const weightSolveResultSchema = z.object({
  /** a weight per symbol of the game: the closest the solver found to the form's */
  weights: z.record(z.string(), z.number()),
  /**
   * whether `rtp.analyticRtp` is within `tolerance` of the target; false:
   * the pays cannot reach it by weights alone and these are the closest found
   */
  reached: z.boolean(),
  targetRtp: z.number(),
  /** RTP_SOLVE_TOLERANCE */
  tolerance: z.number(),
  rtp: weightSolveRtpSchema,
})

/** A game a cabinet can offer: what the cabinet form needs to pick one. */
export const gameOptionSchema = z.object({ id: idSchema, name: z.string() })

export const issuedCabinetKeySchema = z.object({
  /** the secret key: returned exactly once, the backend keeps only a hash */
  key: z.string(),
  keyIssuedAt: z.date(),
})

export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'set_status',
  'issue_key',
  // sessions: sign_in_failed has no actor, its email is in `changes`
  'sign_in',
  'sign_in_failed',
  'sign_out',
  // users (role_change is also a member's tenant role)
  'ban',
  'unban',
  'role_change',
  'password_change',
  'password_reset',
  'email_change',
  // members and invitations
  'member_add',
  'member_remove',
  'invitation_create',
  'invitation_accept',
  'invitation_revoke',
  // games: a new configuration (a copy too), a publish of its draft (the
  // next config version, its name and note), a discarded draft, a new
  // default configuration (platform events; those of a tenant's own
  // configuration belong to that tenant). Draft saves are not audited.
  'configuration_create',
  'configuration_publish',
  'configuration_discard',
  'set_default_configuration',
  // games, before configurations (old events only): a saved config version,
  // a new default version
  'config_version_create',
  'set_default_config',
  // games, before drafts (old events only): a configuration saved straight
  // into place, which was its publish then
  'configuration_update',
  // cabinets: credits booked in by an operator, the whole balance paid out
  'credit_in',
  'credit_out',
  // cabinets: two pages used the cabinet's key at once (no actor: the
  // backend noticed; the two page instance ids are in `changes`)
  'second_page_detected',
] as const
export const auditActionSchema = z.enum(AUDIT_ACTIONS)

export const AUDIT_ENTITIES = [
  'tenant',
  'location',
  'cabinet',
  'session',
  'user',
  'member',
  'invitation',
  'game',
  // the platform's settings (`platformSettings.update`): staff only
  'platform',
] as const
export const auditEntitySchema = z.enum(AUDIT_ENTITIES)

/**
 * The entities whose events tenant owners/admins see in their tenant's log:
 * its members (who joined, left or got another role) and invitations among
 * them. Every other entity (users, sessions, and any new one until it is
 * added here on purpose) is staff-only. A game's events are platform events
 * (no tenant, so no tenant sees them) except those of a tenant's own
 * configurations, which belong to that tenant.
 */
export const TENANT_VISIBLE_AUDIT_ENTITIES = [
  'tenant',
  'location',
  'cabinet',
  'game',
  'member',
  'invitation',
] as const satisfies readonly (typeof AUDIT_ENTITIES)[number][]

/**
 * Who caused an event. A tenant user never learns who on the platform staff
 * acted: they get `staff` without name, email or id. `deleted` once the
 * user's account is gone, `anonymous` when nobody was signed in (a failed
 * sign-in, a second page on a cabinet).
 */
export const auditActorSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('user'),
    id: idSchema,
    name: z.string(),
    email: z.email(),
  }),
  z.object({ kind: z.literal('staff') }),
  z.object({ kind: z.literal('deleted') }),
  z.object({ kind: z.literal('anonymous') }),
])

/** `{ [field]: { from, to } }`. `from` is null on create, `to` null on delete. */
export const auditChangesSchema = z.record(
  z.string(),
  z.object({ from: z.unknown(), to: z.unknown() }),
)

export const auditEventSchema = z.object({
  id: idSchema,
  createdAt: z.date(),
  action: auditActionSchema,
  entity: auditEntitySchema,
  entityId: idSchema,
  changes: auditChangesSchema,
  actor: auditActorSchema,
  /**
   * The record's name: the current one, or for a deleted record the last name
   * the log knows. null when neither exists.
   */
  entityLabel: z.string().nullable(),
  /** false once the record is deleted (nothing to link to) */
  entityExists: z.boolean(),
  /** the tenant the event belongs to; null for platform events (sign-ins, users, games, the platform's settings) */
  tenant: z.object({ id: idSchema, name: z.string() }).nullable(),
})

/**
 * A round of a cabinet: a paid spin and everything that followed from it (its
 * free games, the gamble, the collect), as `rounds.list` returns it.
 */
export const roundSchema = z.object({
  id: idSchema,
  tenantId: idSchema,
  cabinetId: idSchema,
  gameId: idSchema,
  /** the config version the round was played on */
  configId: idSchema,
  configVersion: z.number().int().min(1),
  /** the name of that version's configuration (its current name) */
  configurationName: z.string(),
  /** the stake of the paid spin */
  stake: stakeSchema,
  /** what the paid spin cost */
  stakeTotal: creditsSchema,
  /**
   * every credit the round paid out: a trigger's own win, the collected
   * amount. A win still pending (an open round) is not in it; a lost gamble
   * pays out 0.
   */
  totalWin: creditsSchema,
  /** the free games played in the round (0: none) */
  freeGames: z.number().int().min(0),
  openedAt: z.date(),
  /** null while the round is open (a win pending, free games to play) */
  closedAt: z.date().nullable(),
})

/** A credit movement of a round on the cabinet's ledger. */
export const roundLedgerEntrySchema = z.object({
  /** `bet`: the paid spin's stake; `win`: credits paid out */
  kind: z.enum(['bet', 'win']),
  /** negative for a bet */
  amount: z.number().int(),
  balanceAfter: creditsSchema,
  createdAt: z.date(),
})

/** A round with every play and proof, as `rounds.get` returns it. */
export const roundDetailSchema = roundSchema.extend({
  /** the game server's id of the round, e.g. `local-win-4:r1` (not unique) */
  engineRoundId: z.string(),
  /** the session the round was played in */
  sessionId: z.string(),
  /** the game part of each play reply of the round, in order */
  outcome: z.array(lineSlotPlaySchema),
  /** the proof of each draw of the round (spins and picks), in order */
  proof: z.array(spinProofSchema),
  /** the round's credit movements on the ledger, in order */
  ledger: z.array(roundLedgerEntrySchema),
  /** the paylines of the round's game (its definition): a row index per reel */
  paylines: z.array(z.array(z.number().int().min(0))),
})

/**
 * The window a dashboard card covers: 24 hourly buckets (`24h`) or 7 / 30
 * daily buckets (`7d`, `30d`), in the platform time zone `Europe/Berlin`.
 */
export const DASHBOARD_PERIODS = ['24h', '7d', '30d'] as const
export const dashboardPeriodSchema = z.enum(DASHBOARD_PERIODS)

/**
 * The filters every dashboard card takes. Each one narrows the caller's own
 * scope and never widens it: an id outside it (another tenant's location or
 * cabinet) gives a card with no data, not an error.
 */
export const dashboardInputSchema = z.object({
  period: dashboardPeriodSchema,
  /**
   * The end of the window (exclusive); now when left out. The last bucket is
   * the hour or day the instant before it falls into, and ends at `until`.
   */
  until: z.date().optional(),
  /** the cabinets standing at this location now (history moves with them) */
  locationId: idSchema.optional(),
  cabinetId: idSchema.optional(),
  /**
   * One tenant. Staff without it see every tenant; tenant users without it
   * the tenants they may read (admin-web always sends the host's tenant).
   */
  tenantId: idSchema.optional(),
})

/** One bucket of a card's series: the hour or day starting at `bucketStart`. */
export const dashboardPointSchema = z.object({
  bucketStart: z.date(),
  /** negative for a bucket in which the cabinets paid out more than they took */
  value: z.number().int(),
})

/**
 * A dashboard card: its total over the window and one point per bucket,
 * oldest first, every bucket of the window present (0 for no data).
 */
export const dashboardSeriesSchema = z.object({
  total: z.number().int(),
  series: z.array(dashboardPointSchema),
})

const byIdSchema = z.object({ id: idSchema })

/** the tenant a read is about */
const byTenantSchema = z.object({ tenantId: idSchema })

/** an invitation's link: its id and token */
export const invitationPreviewInputSchema = z.object({
  id: idSchema,
  token: z.string().min(1).max(MAX_INVITATION_TOKEN_LENGTH),
})

export const tenantCreateInputSchema = z.object({
  name: nameSchema,
  slug: slugSchema,
  status: tenantStatusSchema.optional(),
})

export const tenantUpdateInputSchema = z.object({
  id: idSchema,
  name: nameSchema.optional(),
  slug: slugSchema.optional(),
  status: tenantStatusSchema.optional(),
  /** null: back to Use default (the platform's default language) */
  defaultLanguage: inheritableInput(cabinetLanguageSchema),
  /** null: back to Use default (the platform's default admin language) */
  adminLanguage: inheritableInput(adminLanguageSchema),
})

/**
 * A tenant's default configuration of one game, which its cabinets on Use
 * default boot (`cabinetGameConfigSchema`).
 */
export const tenantDefaultConfigurationSchema = z.object({
  gameId: idSchema,
  /**
   * the tenant's default configuration of the game; null: Use default, which
   * is `inheritedConfigurationId` (the game's default configuration; null
   * while it has no current version)
   */
  ...inheritableFields('configurationId', idSchema, idSchema.nullable()),
})

/**
 * A tenant's default configuration of every game a cabinet can offer
 * (`cabinets.gameOptions`), by game name.
 */
export const tenantDefaultConfigurationsSchema = z.object({
  tenantId: idSchema,
  games: z.array(tenantDefaultConfigurationSchema),
})

export const platformSettingsUpdateInputSchema = z.object({
  /** null: back to Use default (built in: en) */
  defaultLanguage: inheritableInput(cabinetLanguageSchema),
  /** null: back to Use default (the browser's language, else en) */
  adminLanguage: inheritableInput(adminLanguageSchema),
})

/** A user's membership: the tenant and the role there. */
export const userMembershipInputSchema = z.object({
  tenantId: idSchema,
  role: tenantRoleSchema,
})

export const userCreateInputSchema = z.object({
  name: nameSchema,
  /** lower case, like Better Auth looks it up; at most 254 characters (RFC 5321) */
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  /** the user signs in with it; staff hand it over */
  password: z.string().min(MIN_PASSWORD_LENGTH).max(MAX_PASSWORD_LENGTH),
  /** the user's platform role; default: none, a tenant user */
  platformRole: platformRoleSchema.nullable().default(null),
  /** a first membership; without one the user is a member of no tenant */
  membership: userMembershipInputSchema.optional(),
})

export const userBanInputSchema = z.object({
  id: idSchema,
  /** why, shown to staff while the ban lasts */
  reason: z.string().trim().min(1).max(MAX_BAN_REASON_LENGTH),
})

export const userSetPlatformRoleInputSchema = z.object({
  id: idSchema,
  /** the user's platform role; null removes it */
  platformRole: platformRoleSchema.nullable(),
})

export const userSetMembershipInputSchema = z.object({
  id: idSchema,
  tenantId: idSchema,
  /** the user's role in the tenant; null removes the membership */
  role: tenantRoleSchema.nullable(),
})

/** a membership (`TenantMember.id`) and the role it gets */
export const memberSetRoleInputSchema = z.object({
  id: idSchema,
  role: z.enum(TENANT_GRANT_ROLES),
})

export const invitationCreateInputSchema = z.object({
  tenantId: idSchema,
  /** lower case, like Better Auth looks it up; at most 254 characters (RFC 5321) */
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  role: tenantRoleSchema,
})

export const locationCreateInputSchema = z.object({
  tenantId: idSchema,
  name: nameSchema,
})

export const locationUpdateInputSchema = z.object({
  id: idSchema,
  name: nameSchema.optional(),
})

/** the most games a cabinet create or update may name (bounds the request) */
export const MAX_CABINET_GAMES = 100

const cabinetGameIdsSchema = z.array(idSchema).max(MAX_CABINET_GAMES)

/**
 * Sets a tenant's default configuration of the games it names, one entry per
 * game at most; the games left out keep theirs. `configurationId` is a
 * configuration of the game the tenant may run (the platform's or its own),
 * or null for Use default (the game's default configuration).
 */
export const tenantDefaultConfigurationsUpdateInputSchema = z.object({
  tenantId: idSchema,
  games: z
    .array(z.object({ gameId: idSchema, configurationId: idSchema.nullable() }))
    .max(MAX_CABINET_GAMES)
    .refine(
      (entries) =>
        new Set(entries.map(({ gameId }) => gameId.toLowerCase())).size ===
        entries.length,
      'one entry per game',
    ),
})

/**
 * The configurations staff and the tenant's owners/admins
 * (`GAME_CONFIGURATION_ROLES`) assign to a cabinet's games: one entry per game
 * at most, each game one the cabinet offers (after the same call),
 * `configurationId` a configuration of that game the cabinet's tenant may
 * run (the platform's or its own) or null for Use default (the tenant's
 * default configuration of the game, else the game's). Replaces every
 * assignment: a game left out is on Use default.
 */
const cabinetGameConfigsInputSchema = z
  .array(z.object({ gameId: idSchema, configurationId: idSchema.nullable() }))
  .max(MAX_CABINET_GAMES)
  .refine(
    (entries) =>
      new Set(entries.map(({ gameId }) => gameId.toLowerCase())).size ===
      entries.length,
    'one entry per game',
  )

export const cabinetCreateInputSchema = z.object({
  tenantId: idSchema,
  locationId: idSchema.nullable().optional(),
  name: nameSchema,
  /** null or omitted: Default */
  language: inheritableInput(cabinetLanguageSchema),
  status: cabinetStatusSchema.optional(),
  /** the games the cabinet offers (at most MAX_CABINET_GAMES); none when omitted */
  gameIds: cabinetGameIdsSchema.optional(),
  /**
   * staff and `GAME_CONFIGURATION_ROLES` (FORBIDDEN for other tenant
   * members): see `cabinetGameConfigsInputSchema`
   */
  gameConfigs: cabinetGameConfigsInputSchema.optional(),
})

/**
 * `LineSlotSettings` as JSON. Any object passes here: the backend sorts
 * and de-duplicates the bet and line options, checks the shape
 * (`lineSlotSettingsSchema`) and the settings against the game's
 * definition, and reports every issue.
 */
const settingsInputSchema = z.record(z.string(), z.unknown())

/** a configuration's note: trimmed, at most MAX_CONFIG_NOTE_LENGTH characters, may be empty */
const configNoteSchema = z.string().trim().max(MAX_CONFIG_NOTE_LENGTH)

/**
 * A new configuration with a draft only (no published version: cabinets
 * cannot run it before its first publish): `settings` as its draft, or a
 * copy of `copyOf` (one of the game's configurations with a published
 * version), whose current published settings become its draft (the
 * source's own draft is left out). With both, `settings` are the draft and
 * `copyOf` names the source (the log, `basedOn`). Either way the new
 * configuration has its own name and note and no link to the source. The
 * draft is stored like `saveDraft` stores one: it may fail the checks.
 * `tenantId` makes it that tenant's (a copy for a tenant starts from a
 * platform configuration or one of the tenant's own); without it, it is a
 * platform configuration. Staff only, either way.
 */
export const gameConfigurationCreateInputSchema = z
  .object({
    gameId: idSchema,
    /** the tenant it belongs to; omitted or null: the platform */
    tenantId: idSchema.nullable().optional(),
    /** unique among the game's configurations of the same owner */
    name: nameSchema,
    note: configNoteSchema.default(''),
    /** its draft's settings */
    settings: settingsInputSchema.optional(),
    /** the configuration whose current published settings it copies (same game) */
    copyOf: idSchema.optional(),
  })
  .refine(
    ({ settings, copyOf }) => settings !== undefined || copyOf !== undefined,
    {
      path: ['settings'],
      message: 'settings or a configuration to copy (copyOf) are required',
    },
  )

/** a draft's revision the edit started from (`gameConfigDraftSummarySchema`) */
const draftRevisionSchema = z.number().int().min(1)

/**
 * A save of a configuration's draft: the whole editor state. The first save
 * creates the draft (`expectedRevision` null: there is none yet), every
 * later one replaces it in place; once another save, publish or discard
 * moved the draft or the published version on, the save is refused
 * (CONFLICT).
 */
export const gameConfigurationSaveDraftInputSchema = z.object({
  id: idSchema,
  /** the draft's revision the edit started from; null: the configuration has no draft */
  expectedRevision: draftRevisionSchema.nullable(),
  /**
   * the configuration's current published version the edit started from;
   * null: never published. What tells a stale edit apart while there is no
   * draft: one from before a publish that left none
   */
  expectedVersion: z.number().int().min(1).nullable(),
  /** applied on publish, then unique among the game's configurations of the same owner */
  name: nameSchema,
  note: configNoteSchema,
  /**
   * `LineSlotSettings` as JSON without `reels` (refused if sent): any
   * object, stored as it is (checked on publish) with the strips the
   * backend builds from its weights
   */
  settings: settingsWithoutReelsSchema,
})

/** A publish or discard of the configuration's draft at `expectedRevision`. */
export const gameConfigurationDraftActionInputSchema = z.object({
  id: idSchema,
  expectedRevision: draftRevisionSchema,
})

export const gameSetDefaultConfigurationInputSchema = z.object({
  gameId: idSchema,
  /** one of the game's platform configurations with a published version */
  configurationId: idSchema,
})

export const cabinetUpdateInputSchema = z.object({
  id: idSchema,
  locationId: idSchema.nullable().optional(),
  name: nameSchema.optional(),
  /** null: back to Default; any tenant user, like the name and location */
  language: inheritableInput(cabinetLanguageSchema),
  /** replaces the games the cabinet offers (at most MAX_CABINET_GAMES) */
  gameIds: cabinetGameIdsSchema.optional(),
  /**
   * replaces the configurations assigned to its games; staff and
   * `GAME_CONFIGURATION_ROLES` (FORBIDDEN for other tenant members), see
   * `cabinetGameConfigsInputSchema`
   */
  gameConfigs: cabinetGameConfigsInputSchema.optional(),
})

// admin-web ↔ backend. Never import this from the client: see README.md.
// Every procedure except system.ping, system.panelLanguage and invitations.preview needs a session
// (Better Auth cookie from `/api/auth/sign-in/email` on the admin API) and
// fails with UNAUTHORIZED (401) without one. Records outside the user's tenants fail with NOT_FOUND.
export const adminContract = {
  system: {
    // public connectivity check
    ping: oc.output(z.object({ serverTime: z.iso.datetime() })),
    /**
     * Public: the language a signed-out page shows. The tenant of `tenantSlug`
     * (null: the apex host), else the platform's default admin language, else
     * the request's `Accept-Language`, else en. An unknown slug answers like
     * a tenant without a default (never NOT_FOUND); a tenant that set a
     * language shows by it, which is no secret.
     */
    panelLanguage: oc
      .input(panelLanguageInputSchema)
      .output(panelLanguageSchema),
    /** staff only (FORBIDDEN otherwise) */
    info: oc.output(systemInfoSchema),
  },
  auth: {
    /** the signed-in user and what they may see */
    me: oc.input(sessionInputSchema).output(sessionInfoSchema),
    /**
     * Sets the signed-in user's own panel language (null: back to Use
     * default); any user, a technician included. Audited as an update of the
     * user. Answers the session like `me`.
     */
    setMyLanguage: oc.input(myLanguageInputSchema).output(sessionInfoSchema),
  },
  tenants: {
    /** staff: every tenant, tenant users: their own (default sort: name) */
    list: oc.input(listQuerySchema).output(listResultSchema(tenantSchema)),
    get: oc.input(byIdSchema).output(tenantSchema),
    /** staff only (FORBIDDEN otherwise); CONFLICT when the slug is taken */
    create: oc.input(tenantCreateInputSchema).output(tenantSchema),
    /**
     * Staff change every field. A tenant's owners (`TENANT_UPDATE_ROLES`) may
     * send `name` and `defaultLanguage`, for their own tenant: FORBIDDEN with
     * `slug` or `status`, and for every other tenant user; another tenant is
     * NOT_FOUND. CONFLICT when the slug is taken.
     */
    update: oc.input(tenantUpdateInputSchema).output(tenantSchema),
    defaultConfigurations: {
      /**
       * The tenant's default configuration of every game a cabinet can
       * offer. Staff and the tenant's `TENANT_DEFAULT_CONFIGURATION_ROLES`:
       * FORBIDDEN for its other users, NOT_FOUND for another tenant.
       */
      get: oc.input(byTenantSchema).output(tenantDefaultConfigurationsSchema),
      /**
       * Sets the tenant's default configuration of the games named; same
       * access as `get`. BAD_REQUEST (with `issues`) for an unknown game, a
       * configuration of another game or tenant, or one never published. The tenant's cabinets on
       * Use default boot it from their next boot with no round open.
       */
      update: oc
        .input(tenantDefaultConfigurationsUpdateInputSchema)
        .output(tenantDefaultConfigurationsSchema),
    },
  },
  platformSettings: {
    /** staff only (FORBIDDEN otherwise) */
    get: oc.output(platformSettingsSchema),
    /**
     * staff only (FORBIDDEN otherwise); tenants and cabinets on Default play
     * in a new `defaultLanguage` from their next boot
     */
    update: oc
      .input(platformSettingsUpdateInputSchema)
      .output(platformSettingsSchema),
  },
  users: {
    /**
     * Every account, staff and tenant users. Staff only (FORBIDDEN
     * otherwise). Sort by `name` (default), `email`, `lastSignInAt`,
     * `createdAt`; filter by `name`, `email` (text), `platformRole` (`eq` /
     * `in` of `admin`, `technician` or `none`), `banned`
     * (booleans, `true` / `false` or the words) and `tenantId` (`eq` / `in`:
     * a member of that tenant).
     */
    list: oc
      .input(listQuerySchema)
      .output(listResultSchema(platformUserSchema)),
    /** staff only (FORBIDDEN otherwise, even for an unknown id); NOT_FOUND for an unknown user */
    get: oc.input(byIdSchema).output(platformUserSchema),
    // The writes go through Better Auth's server API, which audits them
    // (`ban`, `unban`, `role_change`, `member_add`, `member_remove`). Staff
    // only (FORBIDDEN otherwise, before any other check); NOT_FOUND for an
    // unknown user.
    /**
     * A new account with a password (no invitation), staff or a tenant user,
     * with an optional first membership. CONFLICT when the email is taken;
     * BAD_REQUEST for an unknown membership tenant (nothing created).
     */
    create: oc.input(userCreateInputSchema).output(platformUserSchema),
    /**
     * Bans the user without an end, for a reason, and ends their sessions:
     * they cannot sign in until unbanned. BAD_REQUEST for staff banning
     * themselves.
     */
    ban: oc.input(userBanInputSchema).output(platformUserSchema),
    /** lifts a ban; a user who is not banned stays as is */
    unban: oc.input(byIdSchema).output(platformUserSchema),
    /**
     * Sets, changes or (`platformRole: null`) removes a user's platform
     * role. BAD_REQUEST for a platform admin removing or lowering their own;
     * the same value again changes nothing.
     */
    setPlatformRole: oc
      .input(userSetPlatformRoleInputSchema)
      .output(platformUserSchema),
    /**
     * Adds, changes or (`role: null`) removes the user's membership in a
     * tenant. Only staff set `owner`, and a tenant may be left without one.
     * BAD_REQUEST for an unknown tenant; the role the user has, or removing a
     * membership they do not have, changes nothing.
     */
    setMembership: oc
      .input(userSetMembershipInputSchema)
      .output(platformUserSchema),
  },
  members: {
    /**
     * The tenant's members with their users, in the order they joined.
     * Staff and the tenant's `MEMBER_MANAGER_ROLES`: NOT_FOUND for a tenant
     * the user is no member of (staff: an unknown tenant), FORBIDDEN for
     * its other members.
     */
    list: oc.input(byTenantSchema).output(z.array(tenantMemberSchema)),
    /**
     * Sets a member's role (`id`: the membership's) to one of
     * `TENANT_GRANT_ROLES`; the role they have changes nothing. Staff: any
     * member of any tenant, an owner too (only `users.setMembership` makes
     * one). The tenant's `MEMBER_MANAGER_ROLES`: NOT_FOUND for a member of a
     * tenant they are no member of (as for an unknown id), FORBIDDEN for its
     * other members and for an owner, BAD_REQUEST for their own membership.
     * Audited by Better Auth's adapter (`role_change`). Returns the member.
     */
    setRole: oc.input(memberSetRoleInputSchema).output(tenantMemberSchema),
    /**
     * Removes a member (`id`: the membership's) from their tenant, with the
     * same rules and refusals as `setRole`. The user loses access to the
     * tenant at once: every admin API call reads the memberships again, and
     * their sessions' active tenant is cleared where it was this one.
     * Audited by Better Auth's adapter (`member_remove`). Returns the
     * removed member.
     */
    remove: oc.input(byIdSchema).output(tenantMemberSchema),
  },
  invitations: {
    /**
     * The tenant's open invitations (pending and not expired), newest
     * first. Who may read them, and the refusals, as `members.list`.
     */
    list: oc.input(byTenantSchema).output(z.array(invitationSchema)),
    /**
     * Invites `email` to join a tenant with `role` and mails them the accept
     * link. Staff invite to any tenant with any role (BAD_REQUEST for an
     * unknown tenant). A tenant's `MEMBER_MANAGER_ROLES` invite to it with
     * `TENANT_GRANT_ROLES`: NOT_FOUND for a tenant they are no member of,
     * FORBIDDEN for its other members, for the role `owner` and once the
     * tenant has 100 open invitations (Better Auth's limit). The
     * invitation is Better Auth's, so its adapter audits it
     * (`invitation_create`). CONFLICT when the email is a member of the
     * tenant or has an open invitation to it (nothing sent either way;
     * checked before the write, so two calls at once may both invite, like
     * Better Auth's own invite).
     */
    create: oc.input(invitationCreateInputSchema).output(invitationSchema),
    /**
     * Revokes an open invitation: its link stops working at once (its page
     * shows it revoked). Staff: any tenant's. The tenant's
     * `MEMBER_MANAGER_ROLES`: NOT_FOUND for an invitation to a tenant they
     * are no member of (as for an unknown id), FORBIDDEN for its other
     * members and for an invitation as `owner`. CONFLICT once it is no
     * longer open (accepted, rejected, revoked or expired). Audited by
     * Better Auth's adapter (`invitation_revoke`). Returns the invitation,
     * now `canceled`.
     */
    revoke: oc.input(byIdSchema).output(invitationSchema),
    /**
     * Public (no session): the invitation behind an accept link, for its
     * page. NOT_FOUND for an unknown id or a token that is not the
     * invitation's. The invitee signs up and accepts through the auth API
     * (`/sign-up/email`, `/organization/accept-invitation`, with
     * `INVITATION_HEADER`), signed in with the invitation's email.
     */
    preview: oc
      .input(invitationPreviewInputSchema)
      .output(invitationPreviewSchema),
  },
  locations: {
    /** default sort: name */
    list: oc.input(listQuerySchema).output(listResultSchema(locationSchema)),
    get: oc.input(byIdSchema).output(locationSchema),
    create: oc.input(locationCreateInputSchema).output(locationSchema),
    /** the tenant of a location never changes */
    update: oc.input(locationUpdateInputSchema).output(locationSchema),
    /** CONFLICT while cabinets are assigned to the location */
    delete: oc.input(byIdSchema).output(locationSchema),
  },
  cabinets: {
    /** default sort: name */
    list: oc.input(listQuerySchema).output(listResultSchema(cabinetSchema)),
    get: oc.input(byIdSchema).output(cabinetSchema),
    /**
     * BAD_REQUEST when the location is not one of the tenant's locations, a
     * game id is unknown, or a `gameConfigs` entry names a game the cabinet
     * does not offer or a version of another game (`data.issues` at
     * `['gameConfigs', index, field]`); FORBIDDEN for `gameConfigs` from a
     * tenant member outside `GAME_CONFIGURATION_ROLES` (after NOT_FOUND for
     * another tenant)
     */
    create: oc.input(cabinetCreateInputSchema).output(cabinetSchema),
    /**
     * The tenant of a cabinet never changes. `locationId: null` moves it to
     * storage; removing a game drops its assigned config version. Errors as
     * `create`, and CONFLICT for removing a game while a round is open on it.
     * A new `gameConfigs` reaches the cabinet on its next boot with no round
     * open; a new `language` on its next boot (nothing is pushed live).
     */
    update: oc.input(cabinetUpdateInputSchema).output(cabinetSchema),
    /** cabinets are never deleted, they are decommissioned */
    setStatus: oc
      .input(z.object({ id: idSchema, status: cabinetStatusSchema }))
      .output(cabinetSchema),
    /**
     * Issues the cabinet's secret key and returns it once. Replaces (rotates)
     * an existing key immediately. Staff and tenant owners/admins only
     * (FORBIDDEN for other tenant members).
     */
    issueKey: oc.input(byIdSchema).output(issuedCabinetKeySchema),
    /**
     * Books `amount` (hundredths of a credit, one of `CREDIT_IN_AMOUNTS`;
     * BAD_REQUEST otherwise) onto the cabinet's ledger and returns the
     * cabinet with its new balance. Staff and tenant owners/admins only
     * (FORBIDDEN for other tenant members); CABINET_NOT_ACTIVE unless the
     * cabinet is active, CABINET_NOT_IDLE while a round is open.
     */
    creditIn: oc
      .errors(CABINET_CREDIT_ERRORS)
      .input(cabinetCreditInInputSchema)
      .output(cabinetSchema),
    /**
     * Pays out the cabinet's whole balance, which must be `credits` (the
     * balance the operator confirmed), and returns the cabinet (no-op at 0).
     * Same roles and refusals as `creditIn`, then CABINET_CREDITS_CHANGED
     * when the balance is no longer `credits` (nothing paid out).
     */
    cashOut: oc
      .errors(CABINET_CREDIT_ERRORS)
      .input(cabinetCashOutInputSchema)
      .output(cabinetSchema),
    /**
     * every game a cabinet can offer, for any signed-in user: the options of
     * the cabinet form (default sort: name)
     */
    gameOptions: oc
      .input(listQuerySchema)
      .output(listResultSchema(gameOptionSchema)),
  },
  games: {
    /** staff only (FORBIDDEN otherwise); default sort: name */
    list: oc.input(listQuerySchema).output(listResultSchema(gameSchema)),
    /**
     * the game with its definition; staff only (FORBIDDEN otherwise, even
     * for an unknown id); NOT_FOUND for an unknown game
     */
    get: oc.input(byIdSchema).output(gameDetailSchema),
    /**
     * Makes `configurationId` the game's default: cabinets without an
     * assigned configuration whose tenant has no default configuration of
     * the game boot its current version from their next boot with no round
     * open, and every later publish of it the same way (a draft save
     * never). Audited as the game's `set_default_configuration` (a platform
     * event) when the default changes. Staff only (FORBIDDEN otherwise);
     * NOT_FOUND for an unknown game; BAD_REQUEST when the configuration is not one of the game's
     * platform configurations (a tenant's own never is the default) or was
     * never published.
     */
    setDefaultConfiguration: oc
      .input(gameSetDefaultConfigurationInputSchema)
      .output(gameSchema),
    configurations: {
      /**
       * The configurations of the platform's games the viewer may read:
       * every one for staff; for tenant users the platform's (offered to
       * every tenant) and their own tenants' (those they are an owner or
       * admin of): the options of the cabinet form's configuration select.
       * Staff and tenant users with one of `GAME_CONFIGURATION_ROLES` in
       * one of their tenants (FORBIDDEN otherwise; tenant users get no
       * staff `updatedBy`). Without config and versions; filter by `gameId`,
       * `tenantId` (`null`: the platform's) and `availableTo` (`eq` a
       * tenant id only: the configurations that tenant's cabinets may run,
       * the platform's and its own, published ones only), sort by `name` or
       * `updatedAt` (default sort: name). Staff also get the configurations
       * never published (`currentConfigId` null); tenant users never do,
       * and always get `draft` null.
       */
      list: oc
        .input(listQuerySchema)
        .output(listResultSchema(gameConfigurationSchema)),
      /**
       * Staff only (FORBIDDEN otherwise, even for an unknown id); NOT_FOUND
       * for an unknown configuration.
       */
      get: oc.input(byIdSchema).output(gameConfigurationDetailSchema),
      /**
       * A new configuration of the game with a draft only, of `settings` or
       * a copy of `copyOf` (see `gameConfigurationCreateInputSchema`); the
       * default stays as it is. Audited as the game's
       * `configuration_create` (a platform event, or the tenant's for a
       * tenant's configuration; a copy's event names the source as
       * `copiedFrom`). Staff only (FORBIDDEN otherwise), a tenant's
       * configuration too. NOT_FOUND for an unknown game or tenant;
       * BAD_REQUEST when `copyOf` is not one of the game's configurations
       * the new owner may run (a tenant's copy: the platform's or its own)
       * or was never published; CONFLICT when the owner's configurations of
       * the game have the name.
       */
      create: oc
        .input(gameConfigurationCreateInputSchema)
        .output(gameConfigurationDetailSchema),
      /**
       * Saves the whole editor state (name, note, settings) in the
       * configuration's draft: creates it on the first save, else replaces
       * it in place; each save takes the configuration's next `revision`. Any settings object is
       * stored, also one that fails the checks (the draft's `issues` say
       * why). Cabinets keep running the current published version. Not
       * audited. Staff only (FORBIDDEN otherwise, even for an unknown id),
       * a tenant's configuration too. NOT_FOUND for an unknown
       * configuration; CONFLICT when `expectedRevision` is not the draft's
       * revision (null while there is a draft, a number while there is
       * none) or `expectedVersion` not the current published version:
       * another save, publish or discard came first; nothing is saved.
       */
      saveDraft: oc
        .input(gameConfigurationSaveDraftInputSchema)
        .output(gameConfigurationDetailSchema),
      /**
       * Publishes the draft: its settings, checked in full, become the
       * configuration's next version and its current one, its name and note
       * the configuration's, and no draft is left. Cabinets running the
       * configuration get the version at their next boot with no round
       * open. Audited as the game's `configuration_publish` (version and
       * analytic RTP from → to). Staff only (FORBIDDEN otherwise, even for
       * an unknown id). NOT_FOUND for an unknown configuration; BAD_REQUEST
       * when there is no draft, when the draft's settings, name and note
       * equal the current version's (nothing to publish: discard it), or
       * when its settings break their shape, the game's definition or a
       * rule, with `data` as `gameConfigIssuesSchema` (paths start with
       * `settings`); CONFLICT when `expectedRevision` is stale or the
       * owner's configurations of the game have the draft's name.
       */
      publish: oc
        .input(gameConfigurationDraftActionInputSchema)
        .output(gameConfigurationDetailSchema),
      /**
       * Deletes the draft; the configuration stays at its current version.
       * Audited as the game's `configuration_discard`. Staff only
       * (FORBIDDEN otherwise, even for an unknown id). NOT_FOUND for an
       * unknown configuration; BAD_REQUEST when there is no draft;
       * CONFLICT when `expectedRevision` is stale.
       */
      discardDraft: oc
        .input(gameConfigurationDraftActionInputSchema)
        .output(gameConfigurationDetailSchema),
      /**
       * Checks settings as a publish would (shape, the game's
       * definition, rules) on the strips their weights would build from the
       * configuration's draft (else its current published version), and
       * computes their analytic RTP (base game and free games) and trigger
       * odds and their combined engine config, saving nothing: the
       * configuration form's live check of its unsaved settings. Staff only
       * (FORBIDDEN otherwise); NOT_FOUND for an unknown configuration;
       * BAD_REQUEST for settings with `reels`.
       */
      check: oc.input(gameConfigDraftInputSchema).output(gameConfigCheckSchema),
    },
  },
  analysis: {
    /**
     * Queues a simulation of a published version or of a configuration's
     * saved draft (`configId`: the version's or the draft's id, a tenant's
     * own configuration's too; a draft plays the settings of its revision
     * at the start, `draftRevision`), played in a worker thread of the
     * backend, one at a time, in the order of `analysisRunStatusSchema`:
     * poll `get` for its progress and result. Kept in memory only and not
     * audited (it changes nothing a cabinet plays). Staff only (FORBIDDEN
     * otherwise), with any of SIMULATION_ROUNDS; NOT_FOUND for an unknown
     * version or draft; BAD_REQUEST with `data` as `gameConfigIssuesSchema`
     * for a draft that fails the checks; CONFLICT while
     * MAX_QUEUED_SIMULATIONS runs wait.
     */
    start: oc.input(analysisStartInputSchema).output(analysisRunSchema),
    /**
     * The runs of a game the backend still keeps (queued, running, and the
     * finished ones of the last hour), newest first. Staff only (FORBIDDEN
     * otherwise).
     */
    list: oc.input(analysisListInputSchema).output(z.array(analysisRunSchema)),
    /**
     * Staff only (FORBIDDEN otherwise, even for an unknown id); NOT_FOUND
     * for an unknown run or one the backend no longer keeps
     */
    get: oc.input(byIdSchema).output(analysisRunSchema),
    /**
     * Cancels a queued or running run, whoever started it: it fails with an
     * error that says so, and the next run plays at once (a running one's
     * worker is terminated first). Answers the run as it is then; a
     * finished run stays as it is. Not audited, like `start`. Staff only
     * (FORBIDDEN otherwise, even for an unknown id); NOT_FOUND for an
     * unknown run or one the backend no longer keeps
     */
    cancel: oc.input(byIdSchema).output(analysisRunSchema),
    /**
     * Solves the symbol weights for `targetRtp`: the weights closest to
     * `settings.weights` (the editor's unsaved state, as the live check takes
     * it: no `reels`) whose strips, built the way a save builds them from
     * the configuration's draft (else its current published version), have
     * an analytic RTP within RTP_SOLVE_TOLERANCE of it. The pays stay as
     * they are. Answers when done, from a worker thread of the backend;
     * `reached` is false with the closest weights found when the pays cannot
     * reach the target by weights alone. Nothing is stored or audited: save
     * the weights into the draft to keep them. Staff only (FORBIDDEN
     * otherwise); NOT_FOUND for an unknown configuration; BAD_REQUEST with
     * `data` as `gameConfigIssuesSchema` for settings with issues, and for a
     * target outside MIN_TARGET_RTP..MAX_TARGET_RTP; CONFLICT while the
     * backend solves as many as it allows at once; TIMEOUT when a solve takes
     * longer than the backend allows.
     */
    solve: oc.input(weightSolveInputSchema).output(weightSolveResultSchema),
  },
  seeds: {
    /**
     * The first `count` seeds among SEED_SEARCH_LIMIT candidates whose
     * session plays `outcome` on its first spin with the saved version
     * `configId`, found like `make find-seeds` finds them (the same seeds
     * for the same config). Played on the provably fair draw in a worker
     * thread of the backend, answered when done (about a second); nothing is
     * stored or audited. Staff only (FORBIDDEN otherwise); NOT_FOUND for an
     * unknown version; BAD_REQUEST for a draft's id (published versions
     * only), and for `gambleWin` / `gambleLoss` on a
     * version that never gambles a spin win (`gamble.maxAttempts` 0, or
     * 'spin' not in `gamble.wins`); with a cap, gamble seeds are only wins
     * within it; CONFLICT while the backend searches as many as it
     * allows at once; TIMEOUT when a search plays longer than the backend
     * allows (a rare outcome: ask for fewer seeds).
     */
    search: oc.input(seedSearchInputSchema).output(seedSearchResultSchema),
    /**
     * A fresh cabinet to play a found seed on: a tenant of its own (slug
     * `test-<hex>`), a location and an active cabinet offering the version's
     * game with `configId` assigned to it, a key and TEST_CABINET_CREDITS
     * (a credit-in), in one transaction, each step audited as the staff
     * user's. Its first boot with `?seed=` starts a session on that version
     * seeded with it. Staff only (FORBIDDEN otherwise, and FORBIDDEN while
     * the backend runs without ALLOW_TEST_SEEDS: it would ignore the seed);
     * NOT_FOUND for an unknown version; BAD_REQUEST for a draft's id
     * (published versions only) or a version of a tenant's own
     * configuration (only that tenant's cabinets run it).
     */
    createCabinet: oc.input(testCabinetInputSchema).output(testCabinetSchema),
  },
  audit: {
    /**
     * The activity log (default sort: createdAt desc). Staff see every
     * event, platform events (no tenant) included; tenant users the events
     * of `TENANT_VISIBLE_AUDIT_ENTITIES` in the tenants they are an owner or
     * admin of (`ACTIVITY_VIEWER_ROLES`); FORBIDDEN for everyone else. Sort
     * and filter by `createdAt`, `entity`, `action`, `entityId`, `tenantId`
     * and, staff only (BAD_REQUEST for tenant users), `actorUserId` and
     * `userId`: the user an event is about (a user's history), their account
     * (`user`) and their memberships (`member`, also once removed).
     */
    list: oc.input(listQuerySchema).output(listResultSchema(auditEventSchema)),
  },
  rounds: {
    /**
     * The rounds played on cabinets (default sort: openedAt desc). Staff see
     * every round; tenant users the rounds of the tenants they are an owner
     * or admin of (`ROUND_VIEWER_ROLES`); FORBIDDEN for everyone else. Free
     * games are part of their trigger round, never a round of their own.
     * Sort and filter by `openedAt`, `closedAt`, `cabinetId`, `tenantId`,
     * `gameId`, `stakeTotal` and `totalWin` (a date range: `between` with two
     * ISO datetimes with an offset).
     */
    list: oc.input(listQuerySchema).output(listResultSchema(roundSchema)),
    /**
     * A round with its plays, proofs and ledger entries. FORBIDDEN as for
     * `list`; NOT_FOUND for an unknown round or one outside those tenants.
     */
    get: oc.input(byIdSchema).output(roundDetailSchema),
  },
  /**
   * The dashboard's cards, aggregated from the stored play on request (not
   * live). Staff see every tenant; tenant users the tenants they are an owner
   * or admin of (`DASHBOARD_VIEWER_ROLES`); FORBIDDEN for everyone else.
   */
  dashboard: {
    /**
     * Bets minus wins on the cabinets' ledgers (kinds `bet` and `win`) per
     * bucket; the total is the sum of the series.
     */
    profit: oc.input(dashboardInputSchema).output(dashboardSeriesSchema),
    /**
     * The credits on the cabinets at the end of each bucket: the sum of each
     * cabinet's last `balance_after` by then (earlier buckets included). The
     * total is the last bucket's value, so the current credits when `until`
     * is left out.
     */
    credits: oc.input(dashboardInputSchema).output(dashboardSeriesSchema),
    /**
     * The cabinets that opened at least one round in each bucket; the total
     * counts each cabinet once over the whole window.
     */
    activeCabinets: oc
      .input(dashboardInputSchema)
      .output(dashboardSeriesSchema),
  },
}

export type AdminUser = z.infer<typeof adminUserSchema>
export type Membership = z.infer<typeof membershipSchema>
export type SessionInfo = z.infer<typeof sessionInfoSchema>
export type TenantStatus = z.infer<typeof tenantStatusSchema>
export type Tenant = z.infer<typeof tenantSchema>
export type PlatformSettings = z.infer<typeof platformSettingsSchema>
export type TenantDefaultConfiguration = z.infer<
  typeof tenantDefaultConfigurationSchema
>
export type TenantDefaultConfigurations = z.infer<
  typeof tenantDefaultConfigurationsSchema
>
export type PlatformUserMembership = z.infer<
  typeof platformUserMembershipSchema
>
export type PlatformUser = z.infer<typeof platformUserSchema>
export type TenantRole = z.infer<typeof tenantRoleSchema>
export type TenantMember = z.infer<typeof tenantMemberSchema>
export type Invitation = z.infer<typeof invitationSchema>
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>
export type InvitationPreviewStatus = InvitationPreview['status']
export type UserCreateInput = z.input<typeof userCreateInputSchema>
export type Location = z.infer<typeof locationSchema>
export type CabinetStatus = z.infer<typeof cabinetStatusSchema>
export type Cabinet = z.infer<typeof cabinetSchema>
export type CabinetGameConfig = z.infer<typeof cabinetGameConfigSchema>
export type IssuedCabinetKey = z.infer<typeof issuedCabinetKeySchema>
export type Game = z.infer<typeof gameSchema>
export type GameDetail = z.infer<typeof gameDetailSchema>
export type GameDefinition = z.infer<typeof gameDefinitionSchema>
export type PayGroup = z.infer<typeof payGroupSchema>
export type GameOption = z.infer<typeof gameOptionSchema>
export type GameConfigIssues = z.infer<typeof gameConfigIssuesSchema>
export type WeightSolveResult = z.infer<typeof weightSolveResultSchema>
export type GameConfigVersion = z.infer<typeof gameConfigVersionSchema>
export type GameConfigDraftBase = z.infer<typeof configDraftBaseSchema>
export type GameConfigDraftSummary = z.infer<
  typeof gameConfigDraftSummarySchema
>
export type GameConfigDraft = z.infer<typeof gameConfigDraftSchema>
export type GameConfiguration = z.infer<typeof gameConfigurationSchema>
export type GameConfigurationDetail = z.infer<
  typeof gameConfigurationDetailSchema
>
export type SimulationRounds = z.infer<typeof simulationRoundsSchema>
export type AnalysisRunStatus = z.infer<typeof analysisRunStatusSchema>
export type SimulationResult = z.infer<typeof simulationResultSchema>
export type AnalysisRun = z.infer<typeof analysisRunSchema>
export type SeedOutcome = z.infer<typeof seedOutcomeSchema>
export type SeedSearchInput = z.infer<typeof seedSearchInputSchema>
export type SeedFeature = z.infer<typeof seedFeatureSchema>
export type FoundSeed = z.infer<typeof foundSeedSchema>
export type SeedSearchResult = z.infer<typeof seedSearchResultSchema>
export type SystemInfo = z.infer<typeof systemInfoSchema>
export type TestCabinetInput = z.infer<typeof testCabinetInputSchema>
export type TestCabinet = z.infer<typeof testCabinetSchema>
export type GameConfigCheck = z.infer<typeof gameConfigCheckSchema>
export type AuditAction = z.infer<typeof auditActionSchema>
export type AuditEntity = z.infer<typeof auditEntitySchema>
export type AuditActor = z.infer<typeof auditActorSchema>
export type AuditChanges = z.infer<typeof auditChangesSchema>
export type AuditEvent = z.infer<typeof auditEventSchema>
export type Round = z.infer<typeof roundSchema>
export type RoundLedgerEntry = z.infer<typeof roundLedgerEntrySchema>
export type RoundDetail = z.infer<typeof roundDetailSchema>
export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>
export type DashboardInput = z.infer<typeof dashboardInputSchema>
export type DashboardPoint = z.infer<typeof dashboardPointSchema>
export type DashboardSeries = z.infer<typeof dashboardSeriesSchema>
export type AdminContract = typeof adminContract
export type AdminInputs = InferContractRouterInputs<AdminContract>
export type AdminOutputs = InferContractRouterOutputs<AdminContract>
